import { useEffect, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import { Markdown } from 'tiptap-markdown';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';

interface UseDocumentEditorProps {
  editable?: boolean;
  initialContent: string;
  onUpdate?: () => void;
}

export function useDocumentEditor({ onUpdate, initialContent, editable = true }: UseDocumentEditorProps) {
  const lastLoadedContent = useRef<string>('');
  const suppressUpdate = useRef(false);

  const editor = useEditor({
    editable,
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none min-h-[300px] px-4 py-3${!editable ? ' cursor-default' : ''}`,
      },
    },
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Highlight.configure({ multicolor: true }),
      Markdown.configure({
        html: true,
        breaks: true,
        linkify: true,
        tightLists: true,
        bulletListMarker: '-',
        tightListClass: 'tight',
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Placeholder.configure({
        placeholder: 'Report content will appear here...',
      }),
    ],
    onUpdate: () => {
      if (suppressUpdate.current) return;
      onUpdate?.();
    },
  });

  // Load content when the editor is ready and the source content changes.
  // Compare the value directly (do NOT gate on truthiness) so switching to a
  // document with EMPTY content clears the editor instead of leaving the
  // previous document's text on screen (#330). `initialContent` is typed
  // `string`, so the empty case is just `'' !== lastLoaded`. The `false`
  // second arg to setContent is load-bearing: it suppresses the onUpdate /
  // dirty signal for programmatic loads.
  useEffect(() => {
    if (!editor) return;
    if (initialContent === lastLoadedContent.current) return;
    suppressUpdate.current = true;
    editor.commands.setContent(initialContent, false);
    lastLoadedContent.current = initialContent;
    // Re-enable onUpdate on the next frame after React re-renders. Cancel on
    // cleanup so a rapid second load — or an unmount — can't leave a stale
    // callback that flips the flag back while another load is still in flight.
    const raf = requestAnimationFrame(() => {
      suppressUpdate.current = false;
    });
    return () => cancelAnimationFrame(raf);
  }, [editor, initialContent]);

  // Update editable state when it changes
  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  return editor;
}
